// ════════════════════════════════════════════════════════════════════════════
//  Renegociação de contas a receber.
//
//  Renegociar é NOVAÇÃO: a dívida antiga é EXTINTA e substituída por um título
//  NOVO, com novo valor e novo parcelamento. É o OPOSTO da fatura, que só
//  agrupa a MESMA dívida numa cobrança única.
//
//  Os títulos antigos saem do a receber marcados como RENEGOCIADOS — e não como
//  pagos, porque nada foi recebido. A diferença entre o saldo antigo e o valor
//  do acordo é o juro (ou desconto), e a tela mostra isso ANTES de confirmar.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import {
  Search, ChevronLeft, ChevronRight, Handshake, Eye, RotateCcw,
  AlertCircle, TrendingUp, TrendingDown,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useCandidatasRenegociacao, useRenegociacoes, useRenegociacaoDetalhe,
  useRenegociacoesMutations,
} from "@/features/financeiro/hooks/useRenegociacoes";
import { DeleteConfirmDialog } from "@/features/financeiro/components/DeleteConfirmDialog";
import { PessoaSearchCombobox } from "@/components/PessoaSearchCombobox";
import { preverVencimentos } from "@/features/financeiro/schemas/contaReceberSchema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmt = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString("pt-BR") : "—";

const situacaoCor: Record<string, string> = {
  Quitado:     "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  Parcial:     "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20",
  "Em aberto": "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20",
  Cancelada:   "bg-muted text-muted-foreground hover:bg-muted",
};

const Renegociacoes = () => {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.CONTAS_RECEBER, "incluir");
  const podeExcluir = temPermissao(FORMULARIOS.CONTAS_RECEBER, "excluir");

  // ── Aba "Renegociar" ─────────────────────────────────────────────────────
  const [clienteId, setClienteId]       = useState<number | null>(null);
  const [marcadas, setMarcadas]         = useState<number[]>([]);
  const [valorParcela, setValorParcela] = useState("");
  const [qtdParcelas, setQtdParcelas]   = useState("1");
  const [dtVencimento, setDtVencimento] = useState("");
  const [diasParcelas, setDiasParcelas] = useState("");
  const [nota, setNota]                 = useState("");
  const [observacao, setObservacao]     = useState("");

  // ── Aba "Acordos" ────────────────────────────────────────────────────────
  const [busca, setBusca]               = useState("");
  const [pagina, setPagina]             = useState(1);
  const [detalheId, setDetalheId]       = useState<number | null>(null);
  const [estornandoId, setEstornandoId] = useState<number | null>(null);

  const buscaAplicada = useDebounce(busca, 400);
  useEffect(() => { setPagina(1); }, [buscaAplicada]);
  useEffect(() => { setMarcadas([]); }, [clienteId]);

  const { parcelas, isLoading: carregandoCand } = useCandidatasRenegociacao(clienteId);
  const { acordos, total, totalPaginas, isLoading: carregandoAcordos } =
    useRenegociacoes({ pagina, tamanhoPagina: 20, busca: buscaAplicada });
  const { detalhe, isLoading: carregandoDetalhe } = useRenegociacaoDetalhe(detalheId);
  const { renegociar, isRenegociando, estornarRenegociacao, isEstornando } =
    useRenegociacoesMutations();

  // ── Cálculo do acordo ────────────────────────────────────────────────────
  const saldoAntigo = parcelas
    .filter((p) => marcadas.includes(p.parcelaId))
    .reduce((s, p) => s + p.saldo, 0);

  const qtd        = Number(qtdParcelas) || 1;
  const valor      = Number(valorParcela) || 0;
  const valorNovo  = valor * qtd;
  const diferenca  = valorNovo - saldoAntigo;
  const vencimentos = preverVencimentos(dtVencimento, qtd, diasParcelas);

  const podeRenegociar =
    podeIncluir && !!clienteId && marcadas.length > 0 && valor > 0 && !!dtVencimento;

  function toggleParcela(id: number) {
    setMarcadas((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function toggleTodas() {
    setMarcadas((prev) =>
      prev.length === parcelas.length ? [] : parcelas.map((p) => p.parcelaId));
  }

  /** Preenche o acordo com o saldo antigo — ponto de partida sem juros. */
  function usarSaldoComoBase() {
    if (saldoAntigo <= 0) return;
    setValorParcela((saldoAntigo / qtd).toFixed(2));
  }

  function handleRenegociar() {
    if (!clienteId) return;
    renegociar(
      {
        clienteId,
        parcelaIds: marcadas,
        valorParcela: valor,
        qtdParcelas: qtd,
        dtVencimento,
        diasParcelas: diasParcelas || undefined,
        notaRenegociacao: nota ? Number(nota) : undefined,
        observacao: observacao || undefined,
      },
      {
        onSuccess: () => {
          setMarcadas([]); setValorParcela(""); setQtdParcelas("1");
          setDtVencimento(""); setDiasParcelas(""); setNota(""); setObservacao("");
        },
      },
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Renegociação</h1>
        <p className="text-muted-foreground mt-1">
          Substitua títulos em aberto por um novo acordo — a dívida antiga é extinta
        </p>
      </div>

      <Tabs defaultValue="renegociar">
        <TabsList>
          <TabsTrigger value="renegociar">Renegociar</TabsTrigger>
          <TabsTrigger value="acordos">Acordos</TabsTrigger>
        </TabsList>

        {/* ── Renegociar ── */}
        <TabsContent value="renegociar" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="grid gap-2 max-w-md">
                <Label>Cliente *</Label>
                <PessoaSearchCombobox
                  value={clienteId ?? undefined}
                  onChange={(v) => setClienteId(v ?? null)}
                  filter="cliente"
                  placeholder="Buscar cliente..."
                />
              </div>
            </CardHeader>
          </Card>

          {clienteId && (
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Dívida atual */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Títulos em aberto ({parcelas.length})
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Parcelas já faturadas não aparecem — renegociá-las deixaria a
                    fatura cobrando dívida que deixou de existir.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={marcadas.length === parcelas.length && parcelas.length > 0}
                              onCheckedChange={toggleTodas}
                              aria-label="Selecionar todas"
                            />
                          </TableHead>
                          <TableHead>Documento</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {carregandoCand ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Carregando...</TableCell>
                          </TableRow>
                        ) : parcelas.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                              Nenhum título elegível para este cliente
                            </TableCell>
                          </TableRow>
                        ) : parcelas.map((p) => (
                          <TableRow key={p.parcelaId}>
                            <TableCell>
                              <Checkbox
                                checked={marcadas.includes(p.parcelaId)}
                                onCheckedChange={() => toggleParcela(p.parcelaId)}
                                aria-label={`Selecionar parcela ${p.nroParcela}`}
                              />
                            </TableCell>
                            <TableCell className="text-sm">
                              {p.numDocumento || `#${p.tituloId}`}
                              <span className="text-muted-foreground">
                                {" "}({p.nroParcela}/{p.qtdParcelas})
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">
                              {fmt(p.dtVencimento)}
                              {p.diasAtraso > 0 && (
                                <Badge variant="outline" className="ml-2 text-red-500 border-red-500/40">
                                  {p.diasAtraso}d
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{brl(p.saldo)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Novo acordo */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Novo acordo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-md bg-muted/50 p-3 text-sm flex justify-between">
                    <span className="text-muted-foreground">
                      Dívida selecionada ({marcadas.length} parcela(s))
                    </span>
                    <span className="font-semibold">{brl(saldoAntigo)}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="valorParcelaRen">Valor da parcela *</Label>
                      <Input
                        id="valorParcelaRen"
                        type="number" step="0.01" placeholder="0,00"
                        value={valorParcela}
                        onChange={(e) => setValorParcela(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="qtdParcelasRen">Qtd. parcelas</Label>
                      <Input
                        id="qtdParcelasRen"
                        type="number" min={1} max={360}
                        value={qtdParcelas}
                        onChange={(e) => setQtdParcelas(e.target.value)}
                      />
                    </div>
                  </div>

                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={usarSaldoComoBase}
                    disabled={saldoAntigo <= 0}
                  >
                    Usar o saldo atual (sem juros)
                  </Button>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="vencRen">1º vencimento *</Label>
                      <Input
                        id="vencRen" type="date"
                        value={dtVencimento}
                        onChange={(e) => setDtVencimento(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="diasRen">Dias entre parcelas</Label>
                      <Input
                        id="diasRen" placeholder="30 ou 7,14,28"
                        value={diasParcelas}
                        onChange={(e) => setDiasParcelas(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="notaRen">Nº do documento do acordo</Label>
                      <Input
                        id="notaRen" type="number" placeholder="Opcional"
                        value={nota}
                        onChange={(e) => setNota(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="obsRen">Observação</Label>
                      <Input
                        id="obsRen" placeholder="Opcional"
                        value={observacao}
                        onChange={(e) => setObservacao(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* O que muda — mostrado ANTES de confirmar. */}
                  {valorNovo > 0 && marcadas.length > 0 && (
                    <div className="rounded-md border p-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Novo acordo</span>
                        <span className="font-semibold">
                          {qtd}× {brl(valor)} = {brl(valorNovo)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">
                          {diferenca >= 0 ? "Juros" : "Desconto"}
                        </span>
                        <span className={`font-semibold flex items-center gap-1 ${
                          diferenca > 0 ? "text-orange-500"
                          : diferenca < 0 ? "text-blue-500" : ""}`}>
                          {diferenca > 0 && <TrendingUp className="h-3.5 w-3.5" />}
                          {diferenca < 0 && <TrendingDown className="h-3.5 w-3.5" />}
                          {brl(Math.abs(diferenca))}
                        </span>
                      </div>
                      {vencimentos.length > 0 && (
                        <div className="text-xs text-muted-foreground pt-1 border-t">
                          Vencimentos: {vencimentos.slice(0, 4).map((d) =>
                            d.toLocaleDateString("pt-BR")).join(" · ")}
                          {vencimentos.length > 4 && ` · +${vencimentos.length - 4}`}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <span>
                      Os títulos selecionados serão <strong>extintos</strong> e
                      substituídos por este acordo. Eles não são marcados como
                      recebidos — nada entra em caixa aqui.
                    </span>
                  </div>

                  <Button
                    className="w-full gap-2"
                    onClick={handleRenegociar}
                    disabled={!podeRenegociar || isRenegociando}
                  >
                    <Handshake className="h-4 w-4" />
                    {isRenegociando ? "Renegociando..." : "Renegociar"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ── Acordos ── */}
        <TabsContent value="acordos" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
                <div className="flex items-center gap-2">
                  <Handshake className="h-4 w-4 text-muted-foreground" />
                  <CardTitle>Acordos</CardTitle>
                  <span className="text-sm text-muted-foreground">({total})</span>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar cliente ou documento..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Acordo</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Substituídos</TableHead>
                      <TableHead className="text-right">Dívida original</TableHead>
                      <TableHead className="text-right">Acordo</TableHead>
                      <TableHead className="text-right">Diferença</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {carregandoAcordos ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                      </TableRow>
                    ) : acordos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Nenhum acordo registrado
                        </TableCell>
                      </TableRow>
                    ) : acordos.map((a) => {
                      const dif = a.valorRenegociado - a.valorOriginal;
                      return (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">
                            #{a.id}
                            {a.notaRenegociacao && (
                              <span className="text-xs text-muted-foreground ml-1">
                                (doc. {a.notaRenegociacao})
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{a.clienteNome || `Cliente #${a.clienteId}`}</TableCell>
                          <TableCell>{fmt(a.dtRenegociacao)}</TableCell>
                          <TableCell className="text-right">{a.qtdTitulosSubstituidos}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {brl(a.valorOriginal)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {brl(a.valorRenegociado)}
                          </TableCell>
                          <TableCell className={`text-right ${
                            dif > 0 ? "text-orange-500" : dif < 0 ? "text-blue-500" : ""}`}>
                            {dif === 0 ? "—" : brl(Math.abs(dif))}
                          </TableCell>
                          <TableCell>
                            <Badge className={situacaoCor[a.situacao] ?? ""}>{a.situacao}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setDetalheId(a.id)}
                                title="Ver o acordo e o que ele substituiu"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {!a.isCancelado && a.valorPago === 0 && podeExcluir && (
                                <Button
                                  size="sm" variant="ghost"
                                  onClick={() => setEstornandoId(a.id)}
                                  disabled={isEstornando}
                                  title="Estornar (devolve os títulos antigos)"
                                >
                                  <RotateCcw className="h-4 w-4 text-orange-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalPaginas > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Página {pagina} de {totalPaginas} ({total} acordos)
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPagina((p) => p - 1)} disabled={pagina <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Detalhe do acordo ── */}
      <Sheet open={detalheId !== null} onOpenChange={(o) => !o && setDetalheId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Acordo #{detalheId}</SheetTitle>
          </SheetHeader>

          {carregandoDetalhe ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : !detalhe ? (
            <p className="text-sm text-muted-foreground">Acordo não encontrado.</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="font-medium">
                    {detalhe.acordo.clienteNome || `Cliente #${detalhe.acordo.clienteId}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Situação</p>
                  <Badge className={situacaoCor[detalhe.acordo.situacao] ?? ""}>
                    {detalhe.acordo.situacao}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Dívida original</p>
                  <p>{brl(detalhe.acordo.valorOriginal)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Valor do acordo</p>
                  <p className="font-semibold">{brl(detalhe.acordo.valorRenegociado)}</p>
                </div>
              </div>

              <div>
                <p className="font-medium mb-2">
                  Títulos substituídos ({detalhe.titulosSubstituidos.length})
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  Estes títulos foram extintos — não foram recebidos.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Documento</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detalhe.titulosSubstituidos.map((t) => (
                      <TableRow key={t.parcelaId}>
                        <TableCell className="text-sm">
                          {t.numDocumento || `#${t.tituloId}`} ({t.nroParcela}/{t.qtdParcelas})
                        </TableCell>
                        <TableCell className="text-sm">{fmt(t.dtVencimento)}</TableCell>
                        <TableCell className="text-right">{brl(t.valorOriginal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <p className="font-medium mb-2">
                  Parcelas do acordo ({detalhe.parcelasDoAcordo.length})
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parcela</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detalhe.parcelasDoAcordo.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.nroParcela}</TableCell>
                        <TableCell>{fmt(p.dtVencimento)}</TableCell>
                        <TableCell className="text-right">{brl(p.valorReceber)}</TableCell>
                        <TableCell className="text-right">{brl(p.saldo)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <DeleteConfirmDialog
        open={estornandoId !== null}
        onOpenChange={(o) => !o && setEstornandoId(null)}
        onConfirm={() => {
          if (estornandoId !== null)
            estornarRenegociacao(estornandoId, { onSuccess: () => setEstornandoId(null) });
        }}
        title="Estornar a renegociação"
        confirmLabel="Estornar"
        description={
          "Os títulos antigos voltam ao contas a receber no estado original e o " +
          "acordo fica cancelado, preservando a trilha."
        }
      />
    </div>
  );
};

export default Renegociacoes;
